import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { PortalDsButton } from 'src/components/portal-design-system/portal-ds-button';
import { portalDs } from 'src/components/portal-design-system/portal-ds-classes';
import PacingEntityTypeahead from '../pacing-entity-typeahead';
import type { PacingLookupItem } from '../../endpoints/pacing-lookup.types';
import {
  useCreatePacingBudgetMutation,
  useUpdatePacingBudgetMutation,
} from '../../endpoints/pacing-board.endpoints';
import type { PacingBudget, PacingBudgetFormData } from '../../types/pacing-board.types';
import {
  computeNetBudget,
  defaultMarginPct,
} from '../../utils/pacing-budget-utils';

interface PacingBudgetModalProps {
  open: boolean;
  onClose: () => void;
  boardId: string;
  budget?: PacingBudget | null;
  onSaved?: (budgetId: string) => void;
}

const EMPTY_FORM: PacingBudgetFormData = {
  campaign_name: '',
  client: null,
  client_name: '',
  linked_campaign: null,
  linked_insertion_order: null,
  gross_budget: 0,
  margin_pct: 0,
  manual_spent: 0,
  start_date: '',
  end_date: '',
  curve_ideal_fraction: null,
};

const parseNumberInput = (value: string): number | null => {
  if (value.trim() === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatNumberInput = (value?: number | null) => (value == null || value === 0 ? '' : String(value));

const toClientLookupItem = (budget?: PacingBudget | null): PacingLookupItem | null => {
  if (!budget?.client) {
    return null;
  }
  return {
    id: budget.client,
    label: budget.client_name_display,
  };
};

const toCampaignLookupItem = (budget?: PacingBudget | null): PacingLookupItem | null => {
  if (!budget?.linked_campaign) {
    return null;
  }
  return {
    id: budget.linked_campaign.id,
    label: budget.linked_campaign.title,
    linked_campaign_id: budget.linked_campaign.id,
    platform: budget.linked_campaign.platform,
  };
};

const toInsertionOrderLookupItem = (budget?: PacingBudget | null): PacingLookupItem | null => {
  if (!budget?.linked_insertion_order) {
    return null;
  }
  return {
    id: budget.linked_insertion_order,
    label: budget.linked_insertion_order_name ?? budget.campaign_name,
    budget: Number(budget.gross_budget),
    start_date: budget.start_date,
    end_date: budget.end_date,
  };
};

export default function PacingBudgetModal({ open, onClose, boardId, budget, onSaved }: PacingBudgetModalProps) {
  const isEdit = Boolean(budget);
  const [form, setForm] = useState<PacingBudgetFormData>({ ...EMPTY_FORM });
  const [clientInput, setClientInput] = useState('');
  const [campaignLookupInput, setCampaignLookupInput] = useState('');
  const [insertionOrderInput, setInsertionOrderInput] = useState('');
  const [selectedClient, setSelectedClient] = useState<PacingLookupItem | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<PacingLookupItem | null>(null);
  const [selectedInsertionOrder, setSelectedInsertionOrder] = useState<PacingLookupItem | null>(null);
  const [createBudget, { isLoading: isCreating }] = useCreatePacingBudgetMutation();
  const [updateBudget, { isLoading: isUpdating }] = useUpdatePacingBudgetMutation();

  useEffect(() => {
    if (!open) {
      return;
    }

    const clientItem = toClientLookupItem(budget);
    const campaignItem = toCampaignLookupItem(budget);
    const insertionOrderItem = toInsertionOrderLookupItem(budget);

    setSelectedClient(clientItem);
    setSelectedCampaign(campaignItem);
    setSelectedInsertionOrder(insertionOrderItem);
    setClientInput(clientItem?.label ?? budget?.client_name ?? '');
    setCampaignLookupInput(campaignItem?.label ?? '');
    setInsertionOrderInput(insertionOrderItem?.label ?? '');

    if (budget) {
      setForm({
        client: budget.client,
        client_name: budget.client_name,
        campaign_name: budget.campaign_name,
        linked_campaign: budget.linked_campaign?.id ?? null,
        linked_insertion_order: budget.linked_insertion_order,
        gross_budget: Number(budget.gross_budget),
        margin_pct: Number(budget.margin_pct),
        manual_spent: budget.manual_spent ? Number(budget.manual_spent) : 0,
        start_date: budget.start_date,
        end_date: budget.end_date,
        curve_ideal_fraction: budget.curve_ideal_fraction,
      });
      return;
    }

    setForm({ ...EMPTY_FORM });
  }, [open, budget]);

  const handleChange = (field: keyof PacingBudgetFormData, value: unknown) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const applyClientSelection = (item: PacingLookupItem | null) => {
    setSelectedClient(item);
    if (item) {
      setClientInput(item.label);
    }
    setForm((prev) => ({
      ...prev,
      client: item?.id ?? null,
      client_name: item ? null : clientInput.trim() || null,
    }));
  };

  const applyCampaignSelection = (item: PacingLookupItem | null) => {
    setSelectedCampaign(item);
    if (item) {
      setCampaignLookupInput(item.label);
    }
    setForm((prev) => ({
      ...prev,
      linked_campaign: item?.linked_campaign_id ?? item?.id ?? null,
      campaign_name: prev.campaign_name || item?.label || '',
      manual_spent: item ? null : prev.manual_spent ?? 0,
      margin_pct: item?.platform != null ? defaultMarginPct(item.platform) : prev.margin_pct,
    }));
  };

  const applyInsertionOrderSelection = (item: PacingLookupItem | null) => {
    setSelectedInsertionOrder(item);
    if (item) {
      setInsertionOrderInput(item.label);
    }
    setForm((prev) => ({
      ...prev,
      linked_insertion_order: item?.id ?? null,
      gross_budget: item?.budget ?? prev.gross_budget,
      start_date: item?.start_date ?? prev.start_date,
      end_date: item?.end_date ?? prev.end_date,
      campaign_name: prev.campaign_name || item?.label || '',
    }));
  };

  const tracksSpendAutomatically = useMemo(
    () => Boolean(selectedCampaign?.linked_campaign_id ?? form.linked_campaign),
    [form.linked_campaign, selectedCampaign]
  );
  const netBudget = useMemo(
    () => computeNetBudget(form.gross_budget, form.margin_pct),
    [form.gross_budget, form.margin_pct]
  );
  const isSubmitting = isCreating || isUpdating;

  const handleSubmit = async(e: React.FormEvent) => {
    e.preventDefault();

    const payload: PacingBudgetFormData = {
      ...form,
      board_id: boardId,
      client: selectedClient?.id ?? null,
      client_name: selectedClient ? null : clientInput.trim() || null,
      linked_campaign: selectedCampaign?.linked_campaign_id ?? selectedCampaign?.id ?? null,
      linked_insertion_order: selectedInsertionOrder?.id ?? null,
      campaign_name: form.campaign_name.trim(),
      manual_spent: tracksSpendAutomatically ? null : form.manual_spent ?? 0,
    };

    if (isEdit && budget) {
      await updateBudget({ budgetId: budget.id, data: payload }).unwrap();
      onSaved?.(budget.id);
    } else {
      const created = await createBudget(payload).unwrap();
      onSaved?.(created.id);
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => !value && onClose()}
    >
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit budget' : 'Add budget'}
          </DialogTitle>
        </DialogHeader>

        <form
          onSubmit={handleSubmit}
          className="space-y-4"
        >
          <div className={portalDs.field}>
            <label
              className={portalDs.label}
              htmlFor="client-lookup"
            >
              Client
            </label>
            <PacingEntityTypeahead
              entityType="client"
              value={clientInput}
              displayValue={clientInput}
              selectedItem={selectedClient}
              onInputChange={(value) => {
                setClientInput(value);
                setSelectedClient(null);
                handleChange('client', null);
                handleChange('client_name', value);
              }}
              onSelect={applyClientSelection}
              placeholder="Search client..."
              className="w-full"
            />
          </div>

          <div className={portalDs.field}>
            <label
              className={portalDs.label}
              htmlFor="campaign-lookup"
            >
              Campaign link
            </label>
            <PacingEntityTypeahead
              entityType="campaign"
              value={campaignLookupInput}
              displayValue={campaignLookupInput}
              selectedItem={selectedCampaign}
              onInputChange={(value) => {
                setCampaignLookupInput(value);
                setSelectedCampaign(null);
                handleChange('linked_campaign', null);
              }}
              onSelect={applyCampaignSelection}
              placeholder="Search campaign..."
              className="w-full"
            />
          </div>

          <div className={portalDs.field}>
            <label
              className={portalDs.label}
              htmlFor="io-lookup"
            >
              Insertion order link
            </label>
            <PacingEntityTypeahead
              entityType="io"
              value={insertionOrderInput}
              displayValue={insertionOrderInput}
              selectedItem={selectedInsertionOrder}
              onInputChange={(value) => {
                setInsertionOrderInput(value);
                setSelectedInsertionOrder(null);
                handleChange('linked_insertion_order', null);
              }}
              onSelect={applyInsertionOrderSelection}
              placeholder="Search insertion order..."
              className="w-full"
            />
          </div>

          <div className={portalDs.field}>
            <label
              className={portalDs.label}
              htmlFor="campaign-name"
            >
              Budget name
            </label>
            <input
              id="campaign-name"
              className={portalDs.input}
              value={form.campaign_name}
              onChange={(e) => handleChange('campaign_name', e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className={portalDs.field}>
              <label
                className={portalDs.label}
                htmlFor="gross-budget"
              >
                Gross budget
              </label>
              <input
                id="gross-budget"
                type="number"
                min="0"
                step="0.01"
                className={portalDs.input}
                value={formatNumberInput(form.gross_budget)}
                onChange={(e) => handleChange('gross_budget', parseNumberInput(e.target.value) ?? 0)}
                required
              />
            </div>
            <div className={portalDs.field}>
              <label
                className={portalDs.label}
                htmlFor="margin-pct"
              >
                Margin %
              </label>
              <input
                id="margin-pct"
                type="number"
                min="0"
                max="100"
                step="0.01"
                className={portalDs.input}
                value={formatNumberInput(form.margin_pct)}
                onChange={(e) => handleChange('margin_pct', parseNumberInput(e.target.value) ?? 0)}
              />
            </div>
          </div>

          <div className="rounded-lg border border-[#EDEEF2] bg-[#F9FAFB] px-3 py-2 text-sm text-[#5E6278]">
            <span className="font-medium text-[#0D0F1A]">Net budget: </span>
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 0,
            }).format(netBudget || 0)}
            <span className="ml-1 text-xs text-[#9CA3AF]">(platform spend target)</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {!tracksSpendAutomatically && (
              <div className={portalDs.field}>
                <label
                  className={portalDs.label}
                  htmlFor="manual-spent"
                >
                  Spent to date
                </label>
                <input
                  id="manual-spent"
                  type="number"
                  min="0"
                  step="0.01"
                  className={portalDs.input}
                  value={formatNumberInput(form.manual_spent)}
                  onChange={(e) => handleChange('manual_spent', parseNumberInput(e.target.value) ?? 0)}
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className={portalDs.field}>
              <label
                className={portalDs.label}
                htmlFor="start-date"
              >
                Start date
              </label>
              <input
                id="start-date"
                type="date"
                className={portalDs.input}
                value={form.start_date}
                onChange={(e) => handleChange('start_date', e.target.value)}
                required
              />
            </div>
            <div className={portalDs.field}>
              <label
                className={portalDs.label}
                htmlFor="end-date"
              >
                End date
              </label>
              <input
                id="end-date"
                type="date"
                className={portalDs.input}
                value={form.end_date}
                onChange={(e) => handleChange('end_date', e.target.value)}
                required
              />
            </div>
          </div>

          <div className={portalDs.field}>
            <label
              className={portalDs.label}
              htmlFor="curve"
            >
              Ideal curve
            </label>
            <select
              id="curve"
              className={portalDs.select}
              value={form.curve_ideal_fraction == null ? '' : String(form.curve_ideal_fraction)}
              onChange={(e) => {
                const value = e.target.value;
                handleChange('curve_ideal_fraction', value === '' ? null : Number(value));
              }}
            >
              <option value="">
                Straight line (even)
              </option>
              <option value="0.5">
                50% front-loaded
              </option>
              <option value="0.6">
                60% front-loaded
              </option>
              <option value="0.7">
                70% front-loaded
              </option>
            </select>
          </div>

          <DialogFooter>
            <PortalDsButton
              variant="ghost"
              onClick={onClose}
              type="button"
            >
              Cancel
            </PortalDsButton>
            <PortalDsButton
              variant="accent"
              type="submit"
              disabled={isSubmitting}
            >
              {isEdit ? 'Save changes' : 'Add budget'}
            </PortalDsButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
